import { Avatar, Box, Typography } from '@mui/material'
import { dateToDashString } from '../../utils/dateUtility'
import {
  GridRenderCellParams,
  GridValueFormatterParams,
  GridCellParams,
} from '@mui/x-data-grid'
import { TFunction } from 'i18next'
import React from 'react'
import {
  CommunityInviteItemStatusEnum,
  FetchCommunityInvitesStatusEnum,
  EnrollmentInviteProgram,
} from '@swagger'
import { ActionableTableColumn } from '../Table/ActionableTable'

export const COMMUNITY_INVITE_EXPAND_COLUMN_WIDTH = 50
export interface CommunityInviteRow {
  id: string
  familyName: string
  sentToEmail: string
  invitationDate: Date
  resentDate?: Date
  programCount: number
  offeredSpots: number
  skippedSpots: number
  status: CommunityInviteItemStatusEnum
  programs: EnrollmentInviteProgram[]
  parentUserKey?: number
}

export const getCommunityInviteStatusLabel = (
  t: TFunction,
  status?: CommunityInviteItemStatusEnum | FetchCommunityInvitesStatusEnum
): string => {
  switch (status) {
    case CommunityInviteItemStatusEnum.Complete:
      return t('Communities.CommunityInvitesTable.Status.Complete', 'Accepted')

    case CommunityInviteItemStatusEnum.InProgress:
      return t(
        'Communities.CommunityInvitesTable.Status.InProgress',
        'In Progress'
      )

    case CommunityInviteItemStatusEnum.Declined:
      return t('Communities.CommunityInvitesTable.Status.Declined', 'Declined')

    case CommunityInviteItemStatusEnum.Cancelled:
      return t(
        'Communities.CommunityInvitesTable.Status.Cancelled',
        'Cancelled'
      )

    default:
      return t(
        'Communities.CommunityInvitesTable.Status.AllStatuses',
        'All Statuses'
      )
  }
}

const formatDateCell = (params: GridValueFormatterParams<Date | undefined>) => {
  const date = params.value
  return date ? dateToDashString(date, true) : ''
}

const addPointerCell = (params: GridCellParams): string => {
  if (
    !!params.row.parentUserKey &&
    params.row.status === CommunityInviteItemStatusEnum.Complete
  ) {
    return 'MuiDataGrid-cell-pointer'
  }
  return ''
}

export interface GetCommunityInvitesTableColumnsOptions {
  totalOfferedSpots?: number
  totalSkippedSpots?: number
}

export const getCommunityInvitesTableColumns = (
  t: TFunction,
  options?: GetCommunityInvitesTableColumnsOptions
): ActionableTableColumn[] => {
  const { totalOfferedSpots, totalSkippedSpots } = options ?? {}

  const columns: ActionableTableColumn[] = [
    {
      fieldName: 'familyName',
      columnHeaderName: t(
        'Communities.CommunityInvitesTable.Header.FamilyName',
        'Family Name'
      ),
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<CommunityInviteRow, string>
      ) => {
        const familyName = params.value ?? ''
        const familyInitial = familyName.charAt(0).toUpperCase()

        return (
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                fontSize: '1rem',
              }}
            >
              {familyInitial}
            </Avatar>

            <Typography>{familyName}</Typography>
          </Box>
        )
      },
    },
    {
      fieldName: 'sentToEmail',
      columnHeaderName: t(
        'Communities.CommunityInvitesTable.Header.SentEmail',
        'Sent Email'
      ),
      sortable: true,
      flex: 1.5,
      minWidth: 220,
    },
    {
      fieldName: 'invitationDate',
      columnHeaderName: t(
        'Communities.CommunityInvitesTable.Header.InviteDate',
        'Invite Date'
      ),
      sortable: true,
      valueFormatter: formatDateCell,
    },
    {
      fieldName: 'resentDate',
      columnHeaderName: t(
        'Communities.CommunityInvitesTable.Header.ResentDate',
        'Re-sent Date'
      ),
      description: t(
        'Communities.CommunityInvitesTable.Header.ResentDateDescription',
        'Date when this invitation was last resent'
      ),
      valueFormatter: formatDateCell,
    },
    {
      fieldName: 'programCount',
      columnHeaderName: t(
        'Communities.CommunityInvitesTable.Header.Programs',
        'Programs'
      ),
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        const programCount = params.value ?? 0

        if (programCount === 1) {
          return t(
            'Communities.CommunityInvitesTable.ProgramCount',
            '{{count}} Program',
            { count: programCount }
          )
        }

        return t(
          'Communities.CommunityInvitesTable.ProgramCountPlural',
          '{{count}} Programs',
          { count: programCount }
        )
      },
    },
    {
      fieldName: 'offeredSpots',
      columnHeaderName: t(
        'Communities.CommunityInvitesTable.Header.OfferedSpots',
        'Offered Spots'
      ),
      description: t(
        'Communities.CommunityInvitesTable.Header.OfferedSpotsDescription',
        'Total Offered Spots: {{count}}',
        { count: totalOfferedSpots ?? 0 }
      ),
    },
    {
      fieldName: 'skippedSpots',
      columnHeaderName: t(
        'Communities.CommunityInvitesTable.Header.SkippedSpots',
        'Skipped Spots'
      ),
      description: t(
        'Communities.CommunityInvitesTable.Header.SkippedSpotsDescription',
        'Total Skipped Spots: {{count}}',
        { count: totalSkippedSpots ?? 0 }
      ),
    },
    {
      fieldName: 'status',
      columnHeaderName: t(
        'Communities.CommunityInvitesTable.Header.Status',
        'Status'
      ),
      valueFormatter: (
        params: GridValueFormatterParams<CommunityInviteItemStatusEnum>
      ) => getCommunityInviteStatusLabel(t, params.value),
    },
  ]
  return columns.map((col) => ({ ...col, cellClassName: addPointerCell }))
}
